/**
 * Dónde va la tarjeta del tutorial.
 *
 * Pure arithmetic, in its own file, because it carries the promise the whole overlay rests on
 * — **the teacher's words are always on screen** — and a promise that matters that much is
 * worth being able to prove rather than eyeball. `__tests__/colocar.test.ts` runs it over
 * every shape a screen can take, including the absurd ones.
 *
 * The code this replaced picked a side from a ratio: card above the target when the target's
 * bottom passed 55% of the screen, card below otherwise. Then it trusted that it fitted. It
 * often did not. `bottom: innerHeight - marco.top + 12` pins the card's foot just above the
 * target and lets its head go wherever the sentence takes it, so on three of the seventeen
 * lessons the card ran 120 to 204 pixels off the top of the screen: the step counter and the
 * opening lines simply gone, on the one screen whose entire job is to be read. Scrolling made
 * it worse rather than better, because the target climbing the screen dragged the card up
 * with it.
 */

/** The highlighted thing, in viewport pixels. */
export interface Marco {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * The smallest box around all of these: the spotlighted thing and what opens out of it, such as
 * a search's list of results, which is drawn over the page below its box and is still the thing
 * the step asks somebody to touch. Boxes with no size are left out.
 */
export function abarcar(cajas: readonly Marco[]): Marco | null {
  const con = cajas.filter((c) => c.width > 0 || c.height > 0);
  if (con.length === 0) return null;
  const top = Math.min(...con.map((c) => c.top));
  const left = Math.min(...con.map((c) => c.left));
  const abajo = Math.max(...con.map((c) => c.top + c.height));
  const derecha = Math.max(...con.map((c) => c.left + c.width));
  return { top, left, width: derecha - left, height: abajo - top };
}

/** Where the card sits and how tall it may be there. Viewport pixels, both. */
export interface Sitio {
  top: number;
  altoMax: number;
}

/**
 * La parte de la pantalla que de verdad se ve.
 *
 * Not the same thing as `innerHeight`. When a keyboard opens, the height somebody can see
 * shrinks — but whether the layout viewport shrinks with it is up to the browser, and Chrome
 * for Android has not done so by default since it adopted `interactive-widget=resizes-visual`.
 * So on the steps that ask somebody to type — the float, a product name, an amount, a price —
 * `innerHeight` can still report a full screen while half of it is covered by keys, and a card
 * placed against the bottom of that number sits behind them.
 *
 * `visualViewport` reports what is actually visible under either behaviour, which is why the
 * placement works in these terms and not in the window's.
 */
export interface Ventana {
  /** Where the visible part starts, offset inside the layout viewport. Usually 0. */
  top: number;
  alto: number;
}

/** Between the card and the thing it is talking about. */
export const AIRE_TARJETA = 12;

/** The card never comes closer than this to the edge of the screen. */
export const BORDE = 8;

/** Under this a card is not worth reading, so it stops shrinking and starts overlapping. */
export const ALTO_MINIMO = 160;

/**
 * The same floor for a spotlight that must not be covered: the step counter and two lines of
 * the sentence. Less than a comfortable read, but a card lying across a PIN pad leaves a step
 * nobody can finish, and a short card beside it does not.
 */
export const ALTO_MINIMO_SIN_TAPAR = 100;

/**
 * Place the card so that all of it is on screen, preferring to sit beside the spotlight.
 *
 * Order of preference: under the target, over the target, whichever side has more room with
 * the card scrolling inside itself, and — only when neither gap could be read in at all —
 * across the target. Whatever that reasoning concludes, the answer is clamped into the
 * viewport before it is returned. There is no path out of here that puts the card off screen.
 *
 * @param marco      the spotlight, or null when the step has nothing to point at
 * @param alto       how tall the card wants to be, measured, not guessed
 * @param ventana    the height of the window right now
 * @param puedeTapar false for a spotlight somebody has to type on: the card then settles for a
 *                   smaller gap (`ALTO_MINIMO_SIN_TAPAR`) before it lies across it
 */
export function colocar(
  marco: Marco | null,
  alto: number,
  ventana: Ventana,
  puedeTapar = true,
): Sitio {
  const techo = ventana.top + BORDE;
  const suelo = ventana.top + ventana.alto - BORDE;
  const libre = Math.max(0, suelo - techo);

  // No spotlight: the card is the whole message, so put it in the middle.
  if (!marco) {
    return {
      top: Math.max(techo, ventana.top + (ventana.alto - Math.min(alto, libre)) / 2),
      altoMax: libre,
    };
  }

  const pie = marco.top + marco.height;
  const huecoAbajo = suelo - (pie + AIRE_TARJETA);
  const huecoArriba = marco.top - AIRE_TARJETA - techo;

  let top: number;
  let altoMax = libre;

  if (alto <= huecoAbajo) {
    top = pie + AIRE_TARJETA;
  } else if (alto <= huecoArriba) {
    top = marco.top - AIRE_TARJETA - alto;
  } else if (
    Math.max(huecoArriba, huecoAbajo) >= (puedeTapar ? ALTO_MINIMO : ALTO_MINIMO_SIN_TAPAR)
  ) {
    // Neither side has room for all of it. Take the roomier one and let the card scroll
    // inside itself, which keeps the spotlight uncovered.
    if (huecoAbajo >= huecoArriba) {
      top = pie + AIRE_TARJETA;
      altoMax = huecoAbajo;
    } else {
      top = techo;
      altoMax = huecoArriba;
    }
  } else {
    // The target is nearly as tall as the screen, so neither gap can be read in. Covering
    // part of it beats hiding the sentence that says what to do with it. Against the bottom,
    // where the buttons land under a thumb.
    top = suelo - Math.min(alto, libre);
  }

  // A gap is measured from the target, and the target can be off screen — a spotlight above
  // the top of the window leaves `huecoAbajo` larger than the window itself. Capping the cap
  // is what stops that arithmetic turning into a card taller than the screen it sits on.
  altoMax = Math.min(altoMax, libre);

  const usado = Math.min(alto, altoMax);
  return { top: Math.min(Math.max(top, techo), Math.max(techo, suelo - usado)), altoMax };
}
